import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { Building2, Flame, Wifi, Gauge, Droplets, Bell, AlertTriangle, ArrowUpRight, Activity, ShieldCheck, ClipboardList, CheckCircle2, ChevronRight, Users, SlidersHorizontal } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";

function KPI({ icon: Icon, label, value, subtitle, tone = "orange", testid, to }) {
  const gradients = {
    orange: "from-orange-500 to-pink-500",
    green: "from-emerald-400 to-emerald-600",
    red: "from-red-500 to-pink-600",
    blue: "from-sky-400 to-indigo-500",
    amber: "from-amber-400 to-orange-500",
    purple: "from-purple-500 to-indigo-500",
  };
  const inner = (
    <div className="glass p-5 rounded-lg hover:border-orange-500/30 transition-all group h-full" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${gradients[tone]} flex items-center justify-center shadow-lg`}>
          <Icon className="w-5 h-5 text-gray-900" />
        </div>
        <ArrowUpRight className="w-4 h-4 text-gray-500 group-hover:text-orange-400 transition" strokeWidth={2.5} />
      </div>
      <div className="mt-4">
        <div className="text-3xl font-extrabold tracking-tight text-gray-900">{value}</div>
        <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{label}</div>
        {subtitle && <div className="text-[11px] text-gray-500 mt-1">{subtitle}</div>}
      </div>
    </div>
  );
  if (to) return <Link to={to} className="block">{inner}</Link>;
  return inner;
}

function OnboardingChecklist({ s }) {
  const steps = [
    {
      id: "site",
      label: "Add your first site",
      desc: "Sites are the buildings or locations you monitor.",
      to: "/sites",
      done: (s?.total_sites || 0) > 0,
      icon: Building2,
    },
    {
      id: "device",
      label: "Register a device or gateway",
      desc: "Connect your IoT gateway or fire panel to start receiving live data.",
      to: "/devices",
      done: (s?.total_devices || 0) > 0,
      icon: Wifi,
    },
    {
      id: "alert",
      label: "Configure alert rules",
      desc: "Set thresholds so you get notified when something goes wrong.",
      to: "/alerts/settings",
      done: (s?.total_alert_rules || 0) > 0,
      icon: SlidersHorizontal,
    },
    {
      id: "team",
      label: "Invite your team",
      desc: "Add site managers, safety officers and viewers.",
      to: "/users",
      done: (s?.total_users || 0) > 1,
      icon: Users,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="glass rounded-xl p-6 border-l-4 border-orange-500">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Get started with Fire360Pro</h2>
          <p className="text-sm text-gray-500 mt-0.5">{doneCount} of {steps.length} steps complete</p>
        </div>
        <span className="text-3xl font-extrabold text-orange-400">{pct}%</span>
      </div>
      <div className="w-full h-1.5 bg-gray-200 rounded-full mb-5">
        <div className="h-1.5 f360-gradient rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <Link key={step.id} to={step.to}
              className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${step.done
                ? "border-emerald-200 bg-emerald-50/50 opacity-60 cursor-default pointer-events-none"
                : "border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 cursor-pointer"}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${step.done ? "bg-emerald-100" : "f360-gradient"}`}>
                {step.done
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  : <Icon className="w-5 h-5 text-white" strokeWidth={2} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-sm ${step.done ? "line-through text-gray-400" : "text-gray-900"}`}>{step.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{step.desc}</div>
              </div>
              {!step.done && <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function AdminDashboard({ alerts, ack, criticalAlert }) {
  const [s, setS] = useState(null);
  const [assetStats, setAssetStats] = useState(null);
  const { sites: sitesList, selectedOrg, selectedSite } = useSiteContext();
  const { user: currentUser } = useAuth();
  const nav = useNavigate();
  const isPlatform = currentUser?.role === "super_admin" || currentUser?.role === "platform_support";

  const siteFilter = selectedSite && selectedSite !== "all" ? selectedSite : null;

  useEffect(() => {
    const params = {};
    if (selectedOrg) params.org_id = selectedOrg;
    if (siteFilter) params.site_id = siteFilter;
    const load = () => api.get("/stats/admin", { params }).then((r) => setS(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [selectedOrg, siteFilter]);

  useEffect(() => {
    if (siteFilter) {
      api.get(`/assets/stats/${siteFilter}`).then((r) => setAssetStats(r.data)).catch(() => {});
    } else {
      api.get("/assets/stats").then((r) => setAssetStats(r.data)).catch(() => {});
    }
  }, [selectedOrg, siteFilter]);

  // Filter alerts to the selected site when one is pinned
  const visibleAlerts = siteFilter
    ? alerts?.filter((a) => a.site_id === siteFilter)
    : alerts;

  if (!s) return <div className="text-gray-500">Loading…</div>;

  const tone = (g, a, r) => r > 0 ? "red" : a > 0 ? "amber" : "green";
  const breakTankSites = sitesList.filter((st) => (st.break_tank_count || 0) > 0 || (st.total_height_m || 0) >= 90).length;
  const isNewOrg = s.total_sites === 0;

  return (
    <>
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            {siteFilter ? (sitesList.find((s) => s.site_id === siteFilter)?.name || "Site Dashboard") : "Admin Command Center"}
          </h1>
          <span className="px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] uppercase tracking-widest">Live</span>
        </div>
        <p className="text-gray-500 text-sm mt-1">
          {siteFilter ? "Single-site view" : "Multi-site fleet view"} · GR No. MFS-2023/C.R.370/UD-14
        </p>
      </div>

      {!isPlatform && <OnboardingChecklist s={s} />}

      {isNewOrg ? (
        <div className="glass rounded-xl p-10 text-center text-gray-500 border border-dashed border-gray-200">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-500" />
          <div className="font-semibold text-gray-700 mb-1">No sites yet</div>
          <p className="text-sm">Add your first site using the checklist above, then your live dashboard will appear here.</p>
        </div>
      ) : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI testid="kpi-sites" icon={Building2} label="Total Sites" value={s.total_sites} tone="blue" to="/sites" />
        <KPI testid="kpi-devices" icon={Activity} label="Total Devices" value={s.total_devices} tone="purple" to="/devices" />
        <KPI testid="kpi-critical" icon={Flame} label="Open Critical" value={s.open_critical} tone={s.open_critical ? "red" : "green"} subtitle="Unacknowledged" to="/alerts" />
        <KPI testid="kpi-offline" icon={Wifi} label="Offline Devices" value={s.offline_devices} tone={s.offline_devices ? "red" : "green"} to="/devices" />
        <KPI testid="kpi-compliant" icon={ShieldCheck} label="Sites Compliant" value={`${s.compliance_pct}%`} tone="green" subtitle="GR adherence" to="/reports" />
        <KPI testid="kpi-break-tank" icon={Droplets} label="Break Tank Sites" value={breakTankSites} tone="blue" subtitle="DCPR ≥90m" to="/sites" />
        <KPI testid="kpi-total-assets" icon={ClipboardList} label="Total Assets" value={assetStats?.total ?? "—"} tone="purple"
             subtitle={assetStats ? Object.entries(assetStats.by_type).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([k, v]) => `${v} ${k}`).join(" · ") : ""} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass p-5 rounded-lg lg:col-span-2" data-testid="alerts-timeline">
          <h3 className="font-semibold text-lg mb-4">Alerts — Last 30 Days</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={s.alerts_timeline}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="#64748b" fontSize={10} />
              <YAxis stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <Legend />
              <Line type="monotone" dataKey="critical" stroke="#ef4444" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="warning" stroke="#f59e0b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="info" stroke="#4fd1ff" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="glass p-5 rounded-lg" data-testid="recent-alerts">
          <h3 className="font-semibold text-lg mb-4">Recent Alerts</h3>
          <div className="divide-y divide-white/5 max-h-72 overflow-y-auto">
            {visibleAlerts?.slice(0, 8).map((a) => (
              <div key={a.alert_id} className="py-2 flex items-start gap-3">
                <span className={`mt-1.5 w-2 h-2 rounded-full ${a.severity === "critical" ? "bg-red-500 status-pulse-red" : a.severity === "warning" ? "bg-amber-500" : "bg-sky-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{a.title}</div>
                  <div className="text-[11px] text-gray-500 truncate">{new Date(a.created_at).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
            {!visibleAlerts?.length && <div className="text-sm text-gray-500 py-6 text-center">No alerts.</div>}
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-4">Site Health Grid</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.site_health.map((site) => (
            <div key={site.site_id} className="glass p-5 rounded-lg hover:border-orange-500/30 transition cursor-pointer" onClick={() => nav("/acms")} data-testid={`site-health-${site.site_id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <h4 className="font-bold truncate">{site.name}</h4>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">{site.compliance_status}</div>
                </div>
                <span className={`w-3 h-3 rounded-full bg-gradient-to-br ${tone(site.green, site.amber, site.red) === "red" ? "from-red-500 to-pink-600 status-pulse-red" : tone(site.green, site.amber, site.red) === "amber" ? "from-amber-400 to-orange-500" : "from-emerald-400 to-emerald-600 status-pulse-green"} shrink-0`} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-emerald-500/10 rounded-lg py-2"><div className="text-xl font-bold text-emerald-400">{site.green}</div><div className="text-[10px] text-emerald-300/60">OK</div></div>
                <div className="bg-amber-500/10 rounded-lg py-2"><div className="text-xl font-bold text-amber-400">{site.amber}</div><div className="text-[10px] text-amber-300/60">Warn</div></div>
                <div className="bg-red-500/10 rounded-lg py-2"><div className="text-xl font-bold text-red-400">{site.red}</div><div className="text-[10px] text-red-300/60">Crit</div></div>
              </div>
              <div className="mt-3 text-xs flex items-center justify-between">
                <span className="text-gray-500">Open alerts</span>
                <span className={`font-bold ${site.open_alerts ? "text-orange-400" : "text-emerald-400"}`}>{site.open_alerts}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </>
  );
}

function ManagerDashboard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/stats/site-manager").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, []);

  const ack = async (id) => {
    setBusy(id); try { await api.post(`/alerts/${id}/acknowledge`); toast.success("Acknowledged"); load(); } finally { setBusy(null); }
  };

  if (!s?.site) return <div className="glass p-8 rounded-lg text-gray-500">No site assigned to your account yet.</div>;

  const donutData = [
    { name: "Normal", value: s.donut.normal, color: "#22c55e" },
    { name: "Warning", value: s.donut.warning, color: "#f59e0b" },
    { name: "Critical", value: s.donut.critical, color: "#ef4444" },
    { name: "Offline", value: s.donut.offline, color: "#64748b" },
  ].filter((x) => x.value > 0);

  return (
    <>
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold">{s.site.name}</h1>
        <p className="text-gray-500 text-sm mt-1">Site Manager dashboard · {s.site.address}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Activity} label="Total Devices" value={s.total_devices} tone="purple" />
        <KPI icon={Bell} label="Active Alerts" value={s.active_alerts} tone={s.active_alerts ? "amber" : "green"} />
        <KPI icon={Gauge} label="Compliance Score" value={`${s.compliance_score}%`} tone={s.compliance_score >= 80 ? "green" : "red"} />
        <KPI icon={ShieldCheck} label="Last Test" value="Today" tone="blue" subtitle="Auto-monitoring" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass p-5 rounded-lg">
          <h3 className="font-semibold text-lg mb-4">Device Status Breakdown</h3>
          {donutData.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={donutData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" stroke="none">
                  {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-gray-500 text-sm py-12 text-center">No device data</div>}
        </div>
        <div className="glass p-5 rounded-lg lg:col-span-2">
          <h3 className="font-semibold text-lg mb-4">Recent Alerts</h3>
          <div className="divide-y divide-white/5">
            {s.recent_alerts?.slice(0, 10).map((a) => (
              <div key={a.alert_id} className="py-3 flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${a.severity === "critical" ? "bg-red-500 status-pulse-red" : a.severity === "warning" ? "bg-amber-500" : "bg-sky-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{a.title}</div>
                  <div className="text-[11px] text-gray-500">{new Date(a.created_at).toLocaleTimeString()}</div>
                </div>
                {!a.acknowledged && (
                  <Button size="sm" onClick={() => ack(a.alert_id)} disabled={busy === a.alert_id} className="f360-gradient border-0 text-xs">Ack</Button>
                )}
              </div>
            ))}
            {!s.recent_alerts?.length && <div className="text-gray-500 text-sm py-6 text-center">No recent alerts.</div>}
          </div>
        </div>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { alerts, setAlerts, sites, selectedSite } = useSiteContext();

  const siteFilter = selectedSite && selectedSite !== "all" ? selectedSite : null;
  const visibleAlerts = siteFilter ? alerts?.filter((a) => a.site_id === siteFilter) : alerts;
  const criticalAlert = visibleAlerts?.find((a) => !a.acknowledged && a.severity === "critical");

  const ack = async (id) => {
    await api.post(`/alerts/${id}/acknowledge`);
    setAlerts((prev) => prev.map((a) => (a.alert_id === id ? { ...a, acknowledged: true } : a)));
    toast.success("Alert acknowledged");
  };

  const isAdmin = ["admin", "customer_admin", "super_admin"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {criticalAlert && (
        <div className="banner-blink rounded-lg border border-red-500/40 bg-gradient-to-r from-red-500/20 via-orange-500/20 to-pink-500/20 px-5 py-4 flex items-center gap-4" data-testid="critical-banner">
          <AlertTriangle className="w-6 h-6 text-red-400 shrink-0" strokeWidth={2.5} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold uppercase tracking-wider text-red-300">CRITICAL</div>
            <div className="text-base text-gray-900 truncate">
              {criticalAlert.title} at {sites.find((s) => s.site_id === criticalAlert.site_id)?.name} — {new Date(criticalAlert.created_at).toLocaleTimeString()}
            </div>
          </div>
          <Button onClick={() => ack(criticalAlert.alert_id)} className="f360-gradient border-0 shrink-0" data-testid="ack-critical-btn">
            <span className="hidden sm:inline">Acknowledge</span>
            <span className="sm:hidden">Ack</span>
          </Button>
        </div>
      )}

      {isAdmin ? <AdminDashboard alerts={visibleAlerts} ack={ack} criticalAlert={criticalAlert} /> : <ManagerDashboard />}
    </div>
  );
}
